import type {RequestStatus} from '@yandex-delivery/types/schemas/libraries/logistic-platform-definitions/docs/yaml/definitions/common_claims'
import {addQuery, getUUID} from '@yandex-delivery/utils'

import type {CreateDCAARepositoryArguments, DcaaRepository, EmployeeSettingsGetResponse} from './types'

export function createDCAARepository(params: CreateDCAARepositoryArguments): DcaaRepository {
  const {fetch} = params

  return {
    fetchClientRoleList() {
      return fetch({
        method: 'GET',
        url: 'dcaa/delivery/v1/client/role/list',
      })
    },
    fetchCreateAccepted() {
      throw new Error('Function "fetchCreateAccepted" not implemented.')
    },
    fetchPaymentMethods() {
      throw new Error('Function not implemented.')
    },
    fetchPaymentMethodsListV2(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v2/employee/payment-methods/list',
        body,
      })
    },
    fetchFormOrder() {
      throw new Error('Function not implemented.')
    },
    fetchPaymentMethodActualState() {
      return fetch({
        method: 'POST',
        url: 'phoenix/dcaa/cargo/v2/corp/payment-method/actual-state',
        body: {},
      })
    },
    fetchOffersCalculate(body) {
      return fetch({
        method: 'POST',
        url: 'phoenix/dcaa/cargo/v1/offers/calculate',
        body,
      })
    },
    fetchOffersCalculateV2(body, query = {}) {
      return fetch({
        method: 'POST',
        url: addQuery('phoenix/dcaa/cargo/v2/offers/calculate', query),
        body,
      })
    },
    fetchPreOrderRetrieve(query) {
      return fetch({
        url: addQuery('phoenix/dcaa/cargo/v1/pre-orders/retrieve', query),
      })
    },
    fetchPenaltyComplaintGenerate() {
      return fetch({
        method: 'POST',
        url: 'phoenix/dcaa/cargo/v1/penalty-complaint/generate',
        headers: {
          accept: 'application/octet-stream',
        },
        body: {},
      })
    },
    fetchOrderCallFromManager(body) {
      return fetch({
        method: 'POST',
        url: 'dcaa/v1/order-call-from-manager',
        body,
      })
    },
    fetchCardDetails(body) {
      return fetch({
        url: '/dcaa/delivery/v1/card/details',
        method: 'POST',
        body,
      })
    },
    fetchClaimsBulkInfo(body) {
      return fetch({
        url: '/phoenix/cargo-claims/v2/claims/bulk_info',
        method: 'POST',
        body,
      })
    },
    fetchClaimsInfo(claimId) {
      return fetch({
        url: `/phoenix/cargo-claims/v2/claims/info?claim_id=${encodeURIComponent(claimId)}`,
        method: 'POST',
      })
    },
    fetchClaimDraftInfo(draftId) {
      return fetch({
        url: `/phoenix/cargo-claims/v2/claims/info?draft_id=${encodeURIComponent(draftId)}`,
        method: 'POST',
      })
    },
    fetchClaimsPerformerPhoto(claimId) {
      return fetch({
        url: `/dcaa/delivery/v1/claims/performer-photo?claim_id=${encodeURIComponent(claimId)}`,
        method: 'GET',
      })
    },
    fetchClaimsPerformerPosition(claimId) {
      return fetch({
        url: `/cargo-claims/v2/claims/performer-position?claim_id=${encodeURIComponent(claimId)}`,
        method: 'GET',
      })
    },
    fetchDriverVoiceforwarding(claimId) {
      return fetch({
        url: '/phoenix/cargo-claims/v2/driver-voiceforwarding',
        method: 'POST',
        body: {
          claim_id: claimId,
        },
      })
    },
    fetchClaimsSearch(body) {
      return fetch({
        url: '/phoenix/cargo-claims/v2/claims/search',
        method: 'POST',
        body,
      })
    },
    fetchClaimsStatusModel(claimId) {
      return fetch({
        url: `/dcaa/delivery/v1/claims/status-model?claim_id=${encodeURIComponent(claimId)}`,
        method: 'POST',
        body: {},
      })
    },
    fetchClaimDraftStatusModel(draftId) {
      return fetch({
        url: `/dcaa/delivery/v1/claims/status-model?draft_id=${encodeURIComponent(draftId)}`,
        method: 'POST',
        body: {},
      })
    },
    fetchClaimsSearchFilters() {
      return fetch({
        url: '/dcaa/delivery/v1/claims/search/filters',
        method: 'GET',
      })
    },
    fetchClaimsDocument(params) {
      return fetch({
        url: addQuery('/cargo-claims/v2/claims/document', params),
        method: 'GET',
        headers: {
          'content-type': 'application/pdf',
        },
      })
    },
    fetchClaimsTrackingLinks(claimId) {
      return fetch({
        url: addQuery('/dcaa/delivery/v1/claims/tracking-links', {
          claim_id: claimId,
        }),
        method: 'GET',
      })
    },
    fetchClaimsCancel(claimId, body) {
      return fetch({
        method: 'POST',
        url: `/cargo-claims/v2/claims/cancel?claim_id=${encodeURIComponent(claimId)}`,
        body,
      })
    },
    fetchClaimsAccept(claimId, body) {
      return fetch({
        url: `/phoenix/cargo-claims/v2/claims/accept?claim_id=${encodeURIComponent(claimId)}`,
        method: 'POST',
        body,
      })
    },
    fetchLastClaims(body) {
      return fetch({
        url: '/dcaa/delivery/v1/widget/claims',
        method: 'POST',
        body,
      })
    },
    fetchWidgetClaims(body) {
      return fetch({
        url: '/dcaa/delivery/v1/widget/claims',
        method: 'POST',
        body,
      })
    },
    fetchFeedbackSave(body) {
      return fetch({
        method: 'POST',
        url: 'phoenix/dcaa/cargo/v1/feedback/save',
        body,
      })
    },
    fetchBatchCreateStatus() {
      return fetch({
        url: '/cargo-tasks/v1/cargo-tasks/delivery/batch-create/status',
        method: 'POST',
      })
    },
    fetchBatchCreateUpload(file) {
      return fetch({
        url: '/cargo-tasks/v1/cargo-tasks/delivery/batch-create/upload',
        method: 'POST',
        body: file,
        headers: {
          'X-Idempotency-Token': getUUID(),
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      })
    },
    fetchClaimsProofOfDeliveryInfo(claimId) {
      return fetch({
        url: `/cargo-claims/v2/claims/proof-of-delivery/info?claim_id=${encodeURIComponent(claimId)}`,
        method: 'POST',
      })
    },
    fetchUdpCustomerOrderList(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/customer-order/list',
        method: 'POST',
        body,
      })
    },
    fetchUdpCustomerOrderDetails(customerOrderId, body) {
      return fetch({
        url: `/dcaa/delivery/v1/udp/customer-order/details?customer_order_id=${encodeURIComponent(customerOrderId)}`,
        method: 'POST',
        body,
      })
    },
    fetchUdpOrderRepeat(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/order/repeat-payload',
        method: 'POST',
        body,
      })
    },
    fetchUdpOrderDraft(draftId) {
      return fetch({
        url: `/dcaa/delivery/v1/udp/order/repeat-payload?draft_id=${encodeURIComponent(draftId)}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    fetchUdpOrderDraftDetails(draftId, body) {
      return fetch({
        url: `/dcaa/delivery/v1/udp/customer-order/details?draft_id=${encodeURIComponent(draftId)}`,
        method: 'POST',
        body,
      })
    },
    fetchUdpTrackDetails(customerOrderId, body) {
      return fetch({
        url: `/dcaa/delivery/v1/udp/track/details?customer_order_id=${encodeURIComponent(customerOrderId)}`,
        method: 'POST',
        body,
      })
    },
    fetchUdpDraftTrackDetails(draftId, body) {
      return fetch({
        url: `/dcaa/delivery/v1/udp/track/details?draft_id=${encodeURIComponent(draftId)}`,
        method: 'POST',
        body,
      })
    },
    fetchIntegrationClaimsCancel(claimId) {
      return fetch({
        url: '/platform/integration/v1/claims/cancel',
        method: 'POST',
        body: {
          request_id: claimId,
        },
      })
    },
    fetchRequestGetHandoverAct(query) {
      return fetch({
        url: addQuery('/dcaa/platform/v1/request/get-handover-act', query),
        method: 'GET',
      })
    },
    fetchClaimsConfirmationCode(claimId) {
      return fetch({
        url: '/phoenix/cargo-claims/v2/claims/confirmation_code',
        method: 'POST',
        body: {
          claim_id: claimId,
        },
      })
    },
    fetchPerformerInfo(claimId) {
      return fetch({
        url: `/dcaa/delivery/v1/claims/performer-info?claim_id=${encodeURIComponent(claimId)}`,
        method: 'GET',
      })
    },
    fetchRequestDatetimeOptions(requestId) {
      return fetch({
        url: '/dcaa/platform/v1/request/datetime_options',
        method: 'POST',
        body: {
          request_id: requestId,
        },
      })
    },
    fetchRequestRedeliveryOptions(body) {
      return fetch({
        url: '/platform/request/redelivery_options',
        method: 'POST',
        body,
      })
    },
    fetchLocationDetect(location) {
      return fetch({
        url: '/platform/location/detect',
        method: 'POST',
        body: {
          location,
        },
      })
    },
    fetchPickupPointList(body) {
      return fetch({
        url: '/platform/pickup-points/list',
        method: 'POST',
        body,
      })
    },
    fetchRequestEdit(body) {
      return fetch({
        url: '/platform/request/edit',
        method: 'POST',
        body,
      })
    },
    fetchIntegrationSettings() {
      return fetch({
        url: '/platform/integration/v1/settings',
        method: 'GET',
      })
    },
    fetchClaimsActive(query) {
      return fetch({
        url: addQuery('/dcaa/platform/v1/claims/active', query),
        method: 'GET',
        headers: {
          Accept: 'text/csv',
        },
      })
    },
    fetchDefaultCorpClientIds() {
      return fetch({
        method: 'GET',
        url: 'dcaa/country/default_corp_client_ids',
      })
    },
    fetchClaimsApplyChangesRequest(query, body) {
      return fetch({
        method: 'POST',
        url: `/dcaa/delivery/v1/claims/apply-changes/request?request_id=${query.request_id}&claim_id=${query.claim_id}`,
        body,
      })
    },
    fetchChallengePass() {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/client/employee/challenge/pass',
      })
    },
    fetchUdpBoxesEdit(body, queryParams) {
      const {customer_order_id} = queryParams

      return fetch({
        url: `/dcaa/delivery/v1/udp/customer-order/boxes/edit?customer_order_id=${encodeURIComponent(
          customer_order_id,
        )}`,
        method: 'POST',
        body,
      })
    },
    fetchUdpCustomerOrderCancel(customerOrderId, body) {
      return fetch({
        url: addQuery('/dcaa/delivery/v1/udp/customer-order/cancel', {
          customer_order_id: customerOrderId,
        }),
        method: 'POST',
        body,
      })
    },
    fetchUdpCustomerOrderCancelStatus(cancellationRequestId) {
      return fetch({
        url: addQuery('/dcaa/delivery/v1/udp/customer-order/cancel/status', {
          cancellation_request_id: cancellationRequestId,
        }),
        method: 'POST',
      })
    },
    fetchClientOptionList() {
      return fetch({
        url: '/dcaa/delivery/v1/client/option/list',
        method: 'POST',
        body: {},
      })
    },
    editClientOption(body) {
      return fetch({
        url: '/dcaa/delivery/v1/client/option/edit',
        method: 'POST',
        body,
      })
    },
    saveClaimDraft(body, draftId) {
      const url = draftId
        ? `/dcaa/delivery/v1/drafts/upsert?draft_id=${encodeURIComponent(draftId)}`
        : '/dcaa/delivery/v1/drafts/upsert'

      return fetch({
        url,
        method: 'POST',
        body,
      })
    },
    fetchClaimDraftRemove(draftId) {
      return fetch({
        url: `/dcaa/delivery/v1/drafts/remove?draft_id=${encodeURIComponent(draftId)}`,
        method: 'POST',
      })
    },
    fetchClaimDraftAccept(draftId, requestId) {
      return fetch({
        url: `/dcaa/delivery/v1/drafts/accept?draft_id=${encodeURIComponent(draftId)}&request_id=${encodeURIComponent(
          requestId,
        )}`,
        method: 'POST',
      })
    },
    fetchNotificationsAgreementEdit(body) {
      return fetch({
        url: '/dcaa/delivery/v1/notifications-agreements/edit',
        method: 'POST',
        body,
      })
    },
    fetchProvideDataToCrm(body) {
      return fetch({
        url: '/dcaa/delivery/v1/client/provide-data-to-crm',
        method: 'POST',
        body,
      })
    },
    fetchIntegrationOffersInfo(body) {
      return fetch({
        url: '/platform/integration/v1/offers/info',
        method: 'POST',
        body,
      })
    },
    fetchOffersCreate(body) {
      return fetch({
        url: '/dcaa/platform/v1/offers/create',
        method: 'POST',
        body,
      })
    },
    fetchIntegrationOffersConfirm(offerId) {
      return fetch({
        url: '/platform/integration/offers/confirm',
        method: 'POST',
        body: {offer_id: offerId},
      })
    },
    fetchLocationFeatures(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/location/features',
        method: 'POST',
        body,
      })
    },
    fetchCustomDeliveryZonesFetch(body) {
      return fetch({
        url: '/dcaa/v1/custom-delivery-zones/fetch',
        method: 'POST',
        body,
      })
    },
    fetchCustomDeliveryZonesPointsValidate(body) {
      return fetch({
        url: '/dcaa/v1/custom-delivery-zones/points/validate',
        method: 'POST',
        body,
      })
    },
    fetchEditOwnerOptions(body) {
      return fetch({
        url: '/dcaa/delivery/v1/employee/owner/edit',
        method: 'POST',
        body,
      })
    },
    fetchTagList(query) {
      const params = new URLSearchParams()
      query.ids.forEach(id => params.append('ids', id))

      return fetch({
        url: `/dcaa/platform/v1/station/tag/list?${params.toString()}`,
        method: 'GET',
      })
    },
    fetchPickupsCancel(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/pickups/cancel',
        body,
      })
    },
    fetchPickupCalendarDisable(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/pickup-calendars/disable',
        body,
      })
    },
    fetchShipmentEstimate(body) {
      return fetch({
        url: 'dcaa/platform/v1/tag/estimate',
        method: 'POST',
        body,
      })
    },
    fetchPickupPointsList(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/pickup-point/list',
        method: 'POST',
        body,
      })
    },
    fetchPickupFind(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/pickups/find',
        method: 'POST',
        body,
      })
    },
    fetchActivePickups() {
      return fetch({
        url: '/dcaa/delivery/v1/udp/stations/find-active',
        method: 'POST',
      })
    },
    fetchCalcPrice(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/pickups/calc-price',
        method: 'POST',
        body,
      })
    },
    fetchPickupOptions(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/pickups/pickup-options',
        method: 'POST',
        body,
      })
    },
    editPickup(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/pickups/parameters/edit',
        method: 'POST',
        body,
      })
    },
    editCalendarPickup(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/pickup-calendars/regular-pickups/parameters/edit',
        method: 'POST',
        body,
      })
    },
    fetchPickupsCreate(body) {
      return fetch({
        url: 'dcaa/delivery/v1/udp/pickups/create',
        method: 'POST',
        body,
      })
    },
    fetchActiveClaimsList() {
      const activeStatuses: RequestStatus[] = ['new', 'processing', 'returning', 'waiting']
      const queryString = `status=${encodeURIComponent(activeStatuses.join(','))}&take_count=50&use_cursor=true`

      return fetch({
        url: `/dcaa/platform/v1/claims/active?${queryString}`,
        method: 'GET',
      })
    },
    fetchStoragePeriodEdit(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/customer-order/storage-period/edit',
        method: 'POST',
        body,
      })
    },
    fetchEditRequestStatus(editRequestId) {
      return fetch({
        url: `/dcaa/delivery/v1/udp/customer-order/edit/status?editing_request_id=${encodeURIComponent(editRequestId)}`,
        method: 'POST',
      })
    },
    fetchGetEmployeeSettings(): Promise<Readonly<EmployeeSettingsGetResponse>> {
      return fetch({
        url: `/dcaa/delivery/v1/client/employee/settings`,
        method: 'GET',
      })
    },
    fetchSetEmployeeSettings(body): Promise<Readonly<EmployeeSettingsGetResponse>> {
      return fetch({
        url: `/dcaa/delivery/v1/client/employee/settings`,
        method: 'POST',
        body,
      })
    },
    fetchCashOnDeliveryPhoneUpsert(body) {
      return fetch({
        url: '/dcaa/delivery/v1/client/cash-on-delivery-phone/upsert',
        method: 'POST',
        body,
      })
    },
    fetchHandoverInitialize(params) {
      return fetch({
        url: `/dcaa/delivery/v1/robot/handover/initialize?claim_id=${encodeURIComponent(params.claimId)}`,
        method: 'POST',
      })
    },
    fetchHandoverConfirm(params) {
      return fetch({
        url: `/dcaa/delivery/v1/robot/handover/confirm?claim_id=${encodeURIComponent(params.claimId)}`,
        method: 'POST',
      })
    },
    fetchHandoverInfo(params) {
      return fetch({
        url: `/dcaa/delivery/v1/robot/handover/info?claim_id=${encodeURIComponent(params.claimId)}`,
        method: 'POST',
      })
    },
    fetchOffersInfo(body, query) {
      return fetch({
        method: 'POST',
        url: addQuery('/dcaa/platform/v1/offers/info', query),
        body,
      })
    },
    fetchCustomerOrderEdit(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/customer-order/edit',
        body,
      })
    },
    fetchCustomerOrderPriceCalculate(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/customer-order/edit/check-price',
        body,
      })
    },
    fetchFindStationsClusters(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/stations/find-clusters',
        method: 'POST',
        body,
      })
    },
    fetchLkLocationDetect(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/location/detect',
        method: 'POST',
        body,
      })
    },
    fetchBillingPaymentMethods() {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/billing/payment-methods',
        body: {},
      })
    },
    fetchBillingGeneratePdf(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/billing/generate-pdf',
        headers: {
          accept: 'application/pdf',
        },
        body,
      })
    },
    fetchBillingCreateInvoice(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/billing/create-invoice',
        body,
      })
    },
    fetchBillingInvoiceStatus(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/billing/invoice/status',
        body,
      })
    },
    fetchBalanceRefillStartPayment(amount, entityId) {
      return fetch({
        method: 'POST',
        url: `/dcaa/delivery/v1/corp-balance-refill/start-payment?entity_id=${encodeURIComponent(entityId)}`,
        body: {
          amount: amount.toString(),
        },
      })
    },
    fetchBalanceRefillStatus(entityId) {
      return fetch({
        method: 'GET',
        url: `/dcaa/delivery/v1/corp-balance-refill/status?entity_id=${encodeURIComponent(entityId)}`,
      })
    },
    fetchOffers(body) {
      const {_pre_order_id: pre_order_id, ...rest} = body

      return fetch({
        method: 'POST',
        url: pre_order_id
          ? `/phoenix/dcaa/cargo/v2/offers/calculate?pre_order_id=${encodeURIComponent(pre_order_id)}`
          : '/phoenix/dcaa/cargo/v2/offers/calculate',
        body: rest,
      })
    },
    fetchCodWidgetInfo(body) {
      return fetch({
        url: '/dcaa/delivery/v1/udp/widget/cod-accounting',
        method: 'POST',
        body,
      })
    },
    fetchBulkImportState() {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/bulk-uploading/active-upload-state',
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    fetchUploadBulkImportFile(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/bulk-uploading/upload',
        body,
        headers: {
          'X-Idempotency-Token': getUUID(),
        },
      })
    },
    fetchCancelBulkImportUpload(bulkLoadingId) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/bulk-uploading/cancel',
        body: {
          bulk_uploading_id: bulkLoadingId,
        },
      })
    },
    fetchBulkLoadingOrders(bulkLoadingId) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/bulk-uploading/orders-data',
        body: {
          bulk_uploading_id: bulkLoadingId,
        },
      })
    },
    fetchCreateBulkLoadingOrders(bulkLoadingId) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/bulk-uploading/create-orders-async',
        body: {
          bulk_uploading_id: bulkLoadingId,
        },
      })
    },
    fetchCompleteBulkImportUpload(bulkLoadingId) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/bulk-uploading/complete',
        body: {
          bulk_uploading_id: bulkLoadingId,
        },
      })
    },
    fetchDownloadBulkLoadingOrders(bulkLoadingId) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/udp/bulk-uploading/orders-data/download',
        headers: {
          accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        body: {
          bulk_uploading_id: bulkLoadingId,
        },
      })
    },
    fetchGenerateLabelsV2(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v2/udp/generate-labels',
        headers: {
          accept: 'application/pdf',
        },
        body,
      })
    },
    fetchCheckFnsId(body) {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/client/order/check-fns-id',
        body,
      })
    },
    fetchContactInfo() {
      return fetch({
        method: 'POST',
        url: '/dcaa/delivery/v1/ndd-client-manager/contact-info',
      })
    },
  }
}

export * from './__mocks__'
export * from './getters'
export * from './types'
